import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { LevelData, AdditionFact } from '../data/curriculum';
import { Step6SubStep } from '../types';
import { useTTS } from '../hooks/useTTS';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { matchesFactSpeech } from '../utils/speechMatcher';
import { playSuccessSound } from '../utils/sounds';
import { factToSpeechText, factToQuestionSpeechText } from '../data/audioTextGenerator';
import FactDisplay from '../components/FactDisplay';
import AnswerInput from '../components/AnswerInput';
import CountdownTimer from '../components/CountdownTimer';
import MicrophoneIndicator from '../components/MicrophoneIndicator';
import { motion, AnimatePresence } from 'framer-motion';
import TTSPlaybackIndicator from '../components/TTSPlaybackIndicator';

interface Step6CommutativeProps {
  level: LevelData;
  onComplete: () => void;
}

type WeDoPhase = 'original_playing' | 'inverse_playing' | 'countdown' | 'recording' | 'transcribing' | 'success' | 'retry' | 'max_retries';

const MAX_RETRIES_PER_FACT = 3;
const COMMUTATIVE_RULE = 'When you switch the numbers, the answer stays the same.';

/** Given a commutative fact {a,b,s}, return the original primary fact {b,a,s} */
function getOriginalFact(commFact: AdditionFact): AdditionFact {
  return { addend1: commFact.addend2, addend2: commFact.addend1, sum: commFact.sum };
}

const Step6Commutative: React.FC<Step6CommutativeProps> = ({ level, onComplete }) => {
  const [subStep, setSubStep] = useState<Step6SubStep>('ido');
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [retryFact, setRetryFact] = useState<AdditionFact | null>(null);
  const { playText, isLoading, isPlaying, error: ttsError, stop } = useTTS();

  // I Do state
  const [iDoFactIndex, setIDoFactIndex] = useState(-1); // -1 = explanation phase
  const [iDoShowInverse, setIDoShowInverse] = useState(false);
  const [iDoActivePhrase, setIDoActivePhrase] = useState<'example' | 'rule' | null>('example');

  // We Do state
  const [weDoFactIndex, setWeDoFactIndex] = useState(0);
  const [weDoRetryCount, setWeDoRetryCount] = useState(0);
  const [weDoPhase, setWeDoPhase] = useState<WeDoPhase>('original_playing');
  const [weDoShowInverse, setWeDoShowInverse] = useState(false);
  const [weDoCompletedFacts, setWeDoCompletedFacts] = useState<number[]>([]);
  const hasStartedWeDoOriginal = useRef(false);
  const hasStartedWeDoInverse = useRef(false);
  const isMountedRef = useRef(true);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { startListening, isRecording, isTranscribing, micPermissionDenied, volume } = useSpeechRecognition();

  const currentWeDoFact = level.commutativeFacts[weDoFactIndex];
  const weDoExpectedText = currentWeDoFact ? factToSpeechText(currentWeDoFact) : '';

  // Precompute original facts for each commutative fact
  const originalFacts = useMemo(
    () => level.commutativeFacts.map(getOriginalFact),
    [level.commutativeFacts]
  );

  // Generated commutative explanation texts
  const firstOriginal = originalFacts[0];
  const firstCommutative = level.commutativeFacts[0];
  const exampleAudioText = firstCommutative
    ? `If ${factToSpeechText(firstOriginal)}, then ${factToSpeechText(firstCommutative).replace('equals', 'also equals')}`
    : '';

  // Skip entire step if no commutative facts (Level 5: Doubles)
  useEffect(() => {
    if (level.commutativeFacts.length === 0) {
      onComplete();
    }
  }, [level.commutativeFacts.length, onComplete]);

  // Track mount status and cleanup timers on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  // ─── I Do: Sequential explanation + show original then inverse ───
  useEffect(() => {
    if (subStep !== 'ido' || level.commutativeFacts.length === 0) return;

    let cancelled = false;

    const runSequence = async () => {
      // 1. Play commutative explanation in two phrases
      setIDoFactIndex(-1);
      setIDoShowInverse(false);
      setIDoActivePhrase('example');
      await playText(exampleAudioText);
      if (cancelled) return;
      setIDoActivePhrase('rule');
      await playText(COMMUTATIVE_RULE);
      if (cancelled) return;
      setIDoActivePhrase(null);

      // 2. For each commutative fact pair: show original → voiceover → show inverse → voiceover
      for (let i = 0; i < level.commutativeFacts.length; i++) {
        if (cancelled) return;

        // Show original fact
        setIDoFactIndex(i);
        setIDoShowInverse(false);

        // Brief pause for the fact to appear
        await new Promise((r) => setTimeout(r, 400));
        if (cancelled) return;

        // Play voiceover for original fact
        await playText(factToSpeechText(originalFacts[i]));
        if (cancelled) return;

        // Pause before showing inverse
        await new Promise((r) => setTimeout(r, 500));
        if (cancelled) return;

        // Show inverse fact below
        setIDoShowInverse(true);

        // Brief pause for the inverse to appear
        await new Promise((r) => setTimeout(r, 400));
        if (cancelled) return;

        // Play voiceover for inverse fact
        await playText(factToSpeechText(level.commutativeFacts[i]));
        if (cancelled) return;

        // Pause showing both facts
        await new Promise((r) => setTimeout(r, 1200));
        if (cancelled) return;
      }

      // 3. Advance to We Do
      if (!cancelled) {
        setTimeout(() => {
          if (!cancelled) setSubStep('wedo');
        }, 500);
      }
    };

    runSequence();
    return () => {
      cancelled = true;
      stop();
    };
  }, [subStep, level.commutativeFacts, originalFacts, exampleAudioText, playText, stop]);

  // ─── We Do: Play original fact voiceover ───
  useEffect(() => {
    if (subStep !== 'wedo' || weDoPhase !== 'original_playing' || hasStartedWeDoOriginal.current) return;
    hasStartedWeDoOriginal.current = true;

    let cancelled = false;
    setWeDoShowInverse(false);

    const play = async () => {
      await playText(factToSpeechText(originalFacts[weDoFactIndex]));
      if (cancelled) return;
      // Pause, then show inverse
      await new Promise((r) => setTimeout(r, 500));
      if (cancelled) return;
      setWeDoPhase('inverse_playing');
    };
    play();

    return () => {
      cancelled = true;
      stop();
      hasStartedWeDoOriginal.current = false;
    };
  }, [subStep, weDoPhase, weDoFactIndex, originalFacts, playText, stop]);

  // ─── We Do: Show inverse + play its voiceover → countdown ───
  useEffect(() => {
    if (subStep !== 'wedo' || weDoPhase !== 'inverse_playing' || hasStartedWeDoInverse.current) return;
    hasStartedWeDoInverse.current = true;

    let cancelled = false;
    setWeDoShowInverse(true);

    const play = async () => {
      await playText(weDoExpectedText);
      if (cancelled) return;
      await playText(weDoFactIndex === 0 ? "Now it's your turn! Say it out loud." : "Your turn");
      if (cancelled) return;
      setWeDoPhase('countdown');
    };
    play();

    return () => {
      cancelled = true;
      stop();
      hasStartedWeDoInverse.current = false;
    };
  }, [subStep, weDoPhase, weDoExpectedText, weDoFactIndex, playText, stop]);

  // We Do: Auto-advance on success only
  useEffect(() => {
    if (subStep !== 'wedo') return;
    if (weDoPhase === 'success') {
      const timer = setTimeout(() => {
        setWeDoCompletedFacts((prev) => [...prev, weDoFactIndex]);

        if (weDoFactIndex < level.commutativeFacts.length - 1) {
          setWeDoFactIndex((prev) => prev + 1);
          setWeDoRetryCount(0);
          setWeDoShowInverse(false);
          setWeDoPhase('original_playing');
          hasStartedWeDoOriginal.current = false;
          hasStartedWeDoInverse.current = false;
        } else {
          setSubStep('youdo');
          setCurrentFactIndex(0);
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [subStep, weDoPhase, weDoFactIndex, level.commutativeFacts.length]);

  // We Do: Auto-advance from retry back to countdown
  useEffect(() => {
    if (subStep === 'wedo' && weDoPhase === 'retry') {
      const timer = setTimeout(() => setWeDoPhase('countdown'), 1500);
      return () => clearTimeout(timer);
    }
  }, [subStep, weDoPhase]);

  // We Do: Handle mic error — skip to You Do
  useEffect(() => {
    if (subStep === 'wedo' && micPermissionDenied && weDoPhase === 'recording') {
      setSubStep('youdo');
      setCurrentFactIndex(0);
    }
  }, [subStep, micPermissionDenied, weDoPhase]);

  const handleWeDoCountdownComplete = useCallback(async () => {
    setWeDoPhase('recording');

    const { transcript, micDenied } = await startListening();

    if (!isMountedRef.current) return;

    if (micDenied) {
      setSubStep('youdo');
      setCurrentFactIndex(0);
      return;
    }

    setWeDoPhase('transcribing');

    if (matchesFactSpeech(transcript, weDoExpectedText)) {
      playSuccessSound();
      setWeDoPhase('success');
    } else {
      const newRetryCount = weDoRetryCount + 1;
      setWeDoRetryCount(newRetryCount);

      if (newRetryCount >= MAX_RETRIES_PER_FACT) {
        setWeDoPhase('max_retries');
      } else {
        setWeDoPhase('retry');
      }
    }
  }, [startListening, weDoExpectedText, weDoRetryCount]);

  // You Do: Play question audio when moving to a new fact (skip in retry mode — student just heard the answer)
  useEffect(() => {
    if (subStep !== 'youdo') return;
    if (retryFact) return;
    const fact = level.commutativeFacts[currentFactIndex];
    if (fact) {
      playText(factToQuestionSpeechText(fact));
    }
  }, [subStep, currentFactIndex, retryFact]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(
    (answer: number) => {
      if (isAdvancing) return;
      const currentFact = level.commutativeFacts[currentFactIndex];
      const displayedYouDoFact = retryFact || currentFact;

      if (answer === displayedYouDoFact.sum) {
        playSuccessSound();
        setFeedback('correct');
        setIsAdvancing(true);

        advanceTimerRef.current = setTimeout(() => {
          setFeedback(null);
          setIsAdvancing(false);
          setRetryFact(null);

          if (currentFactIndex < level.commutativeFacts.length - 1) {
            setCurrentFactIndex((prev) => prev + 1);
          } else {
            onComplete();
          }
        }, 800);
      } else {
        setFeedback('incorrect');
        setIsAdvancing(true);
        playText(factToSpeechText(displayedYouDoFact));

        feedbackTimerRef.current = setTimeout(() => {
          setFeedback(null);
          setIsAdvancing(false);
          setRetryFact(displayedYouDoFact);
        }, 1500);
      }
    },
    [currentFactIndex, level.commutativeFacts, onComplete, isAdvancing, retryFact]
  );

  if (level.commutativeFacts.length === 0) {
    return null;
  }

  // ─── I Do sub-step ───
  if (subStep === 'ido') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-10 rounded-2xl shadow-lg text-center max-w-lg w-full"
      >
        <div className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">
          I Do
        </div>

        {iDoFactIndex === -1 ? (
          <>
            <div className="flex flex-col items-center gap-4 mb-6">
              <motion.div
                animate={{ opacity: iDoActivePhrase === 'example' ? 1 : 0.45 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center gap-3"
              >
                <FactDisplay fact={firstOriginal} showAnswer={true} size="large" />
                <FactDisplay fact={firstCommutative} showAnswer={true} size="large" />
              </motion.div>
              {iDoActivePhrase === 'rule' && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-xl leading-relaxed text-gray-700"
                >
                  {COMMUTATIVE_RULE}
                </motion.p>
              )}
            </div>
            <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-4 mb-6">
              <FactDisplay fact={originalFacts[iDoFactIndex]} showAnswer={true} size="large" />
              {iDoShowInverse && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FactDisplay fact={level.commutativeFacts[iDoFactIndex]} showAnswer={true} size="large" />
                </motion.div>
              )}
            </div>
            <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />
          </>
        )}
      </motion.div>
    );
  }

  // ─── We Do sub-step — speech recognition ───
  if (subStep === 'wedo') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-lg w-full"
      >
        <div className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-2">
          We Do
        </div>

        {/* Current fact pair: original + inverse */}
        <div className="flex flex-col items-center gap-3 mb-6">
          <motion.div animate={{ opacity: weDoShowInverse ? 0.35 : 1, scale: weDoShowInverse ? 0.9 : 1 }} transition={{ duration: 0.3 }}>
            <FactDisplay fact={originalFacts[weDoFactIndex]} showAnswer={true} />
          </motion.div>
          {weDoShowInverse && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FactDisplay fact={level.commutativeFacts[weDoFactIndex]} showAnswer={true} />
            </motion.div>
          )}
        </div>

        {/* Completed facts ladder */}
        {weDoCompletedFacts.length > 0 && (
          <div className="flex flex-col items-center gap-2 mb-4">
            {weDoCompletedFacts.map((idx) => {
              const fact = level.commutativeFacts[idx];
              return (
                <motion.div
                  key={`${fact.addend1}_${fact.addend2}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.45, scale: 0.9 }}
                  className="flex items-center gap-2"
                >
                  <FactDisplay fact={fact} showAnswer={true} />
                  <span className="text-green-500 text-xs">&#10003;</span>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Phase-specific UI */}
        <AnimatePresence mode="wait">
          {(weDoPhase === 'original_playing' || weDoPhase === 'inverse_playing') && (
            <motion.div key="playing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />
            </motion.div>
          )}

          {weDoPhase === 'countdown' && (
            <motion.div key="countdown" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p className="text-gray-500 text-sm mb-2">Get ready to speak!</p>
              <CountdownTimer onComplete={handleWeDoCountdownComplete} />
            </motion.div>
          )}

          {(weDoPhase === 'recording' || weDoPhase === 'transcribing') && (
            <motion.div key="recording" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <MicrophoneIndicator isRecording={isRecording} isTranscribing={isTranscribing} volume={volume} />
            </motion.div>
          )}

          {weDoPhase === 'success' && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-green-500 font-bold text-lg"
            >
              Correct!
            </motion.div>
          )}

          {weDoPhase === 'retry' && (
            <motion.div
              key="retry"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-amber-500 font-bold text-lg"
            >
              Let's try again!
            </motion.div>
          )}

          {weDoPhase === 'max_retries' && (
            <motion.div
              key="max_retries"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-3"
            >
              <p className="text-indigo-500 font-medium text-lg">Let's try again when you're ready!</p>
              <button
                onClick={() => { setWeDoRetryCount(0); setWeDoPhase('countdown'); }}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Try Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  // ─── You Do sub-step ───
  const currentFact = level.commutativeFacts[currentFactIndex];
  const displayedYouDoFact = retryFact || currentFact;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md w-full"
    >
      <div className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-2">
        You Do
      </div>

      <div className="mb-6">
        {retryFact ? (
          <FactDisplay fact={displayedYouDoFact} showAnswer={true} size="large" />
        ) : (
          <FactDisplay fact={displayedYouDoFact} showAnswer={false} size="large" />
        )}
      </div>

      <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />

      <AnimatePresence mode="wait">
        {feedback === 'correct' && (
          <motion.div
            key="correct"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-green-500 font-bold text-lg mb-4"
          >
            Correct!
          </motion.div>
        )}
        {feedback === 'incorrect' && (
          <motion.div
            key="incorrect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-red-500 font-bold text-lg mb-4"
          >
            The answer is {displayedYouDoFact.sum}
          </motion.div>
        )}
        {feedback === null && <div className="h-[28px] mb-4" />}
      </AnimatePresence>

      <AnswerInput
        answer={displayedYouDoFact.sum}
        onSubmit={handleSubmit}
        disabled={isAdvancing}
        feedback={feedback}
        resetKey={retryFact ? `retry_${currentFactIndex}` : currentFactIndex}
      />
    </motion.div>
  );
};

export default Step6Commutative;
